/**
 * Chart widgets: pie, donut, bar, column, line.
 *
 * All five share one data shape — a category breakdown — so they share one
 * loader and differ only in how they draw. Charts are rendered directly (SVG
 * for pie/donut/column/line, flex rows for bar) rather than through a charting
 * library: the project has no chart dependency and the brief asks not to add
 * unnecessary ones.
 *
 * The cartesian charts MEASURE their tile and draw at that exact pixel size,
 * so bars thicken as the widget grows instead of letterboxing. Pie/donut still
 * scale via `viewBox`, because a circle has no dead space to reclaim. So
 * resizing a widget in the layout builder reflows the drawing.
 */

import { useSourceFields } from './useSourceFields';
import { useContainerSize } from './DashboardGrid';
import { useWidgetData } from './useWidgetData';
import WidgetFrame from './WidgetFrame';
import {
  combineWhere,
  useIncomingConstraint,
  type ActionSourceProps,
  type ActionTargetProps,
  useOutgoingSignal,
} from './useActions';
import { getDataSourceAdapter, type CategoryResult } from '../services/dashboardDataService';
import {
  buildWhereClause,
  formatCompact,
  formatNumber,
  paletteColor,
  validateWidget,
} from '../utils/dashboardUtils';
import type { DashboardDataSource, DashboardWidget, WidgetType } from '../types/dashboardTypes';

interface Props {
  widget: DashboardWidget;
  source?: DashboardDataSource;
  refreshInterval: number;
  accentColor: string;
  dividerColor?: string;
  selectorWhere?: string;
}

interface Datum {
  category: string;
  value: number;
}

/* ─────────────────────────────── Pie / donut ────────────────────────────── */

function polar(cx: number, cy: number, radius: number, angle: number) {
  const radians = ((angle - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function slicePath(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  start: number,
  end: number,
): string {
  const largeArc = end - start > 180 ? 1 : 0;
  const outerStart = polar(cx, cy, outer, start);
  const outerEnd = polar(cx, cy, outer, end);

  if (inner <= 0) {
    return `M ${cx} ${cy} L ${outerStart.x} ${outerStart.y} A ${outer} ${outer} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y} Z`;
  }

  const innerEnd = polar(cx, cy, inner, end);
  const innerStart = polar(cx, cy, inner, start);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outer} ${outer} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${inner} ${inner} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function PieChart({
  data,
  palette,
  donut,
  showLegend,
  onSelect,
  selected,
}: {
  data: Datum[];
  palette?: string[];
  donut: boolean;
  showLegend: boolean;
} & Selectable) {
  const total = data.reduce((sum, entry) => sum + entry.value, 0);
  if (total <= 0) return <EmptyChart />;

  let angle = 0;
  const slices = data.map((entry, index) => {
    const sweep = (entry.value / total) * 360;
    const path = slicePath(50, 50, 44, donut ? 26 : 0, angle, angle + sweep);
    angle += sweep;
    return { path, color: paletteColor(palette, index), entry };
  });

  return (
    <div className="flex h-full min-h-0 items-center gap-2 p-2">
      <svg viewBox="0 0 100 100" className="h-full max-h-full flex-shrink-0" style={{ aspectRatio: '1' }}>
        {slices.map((slice) => (
          <path
            key={slice.entry.category}
            d={slice.path}
            fill={slice.color}
            opacity={selectionOpacity(selected, slice.entry.category)}
            onClick={onSelect ? () => onSelect(slice.entry.category) : undefined}
            style={onSelect ? { cursor: 'pointer' } : undefined}
          >
            <title>{`${slice.entry.category}: ${formatNumber(slice.entry.value)}`}</title>
          </path>
        ))}
        {donut && (
          <text
            x={50}
            y={53}
            textAnchor="middle"
            style={{ fontSize: 13, fontWeight: 600 }}
            className="fill-slate-900"
          >
            {formatCompact(total)}
          </text>
        )}
      </svg>

      {showLegend && (
        <ul className="min-w-0 flex-1 space-y-1 overflow-y-auto pr-1 text-[11px]">
          {slices.map((slice) => (
            <li key={slice.entry.category}>
              <button
                type="button"
                disabled={!onSelect}
                onClick={onSelect ? () => onSelect(slice.entry.category) : undefined}
                aria-pressed={selected === slice.entry.category}
                className={`flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left ${
                  onSelect ? 'cursor-pointer hover:bg-slate-100' : 'cursor-default'
                }`}
                style={{ opacity: selectionOpacity(selected, slice.entry.category) }}
              >
                <span
                  className="h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="truncate text-slate-600" title={slice.entry.category}>
                  {slice.entry.category}
                </span>
                <span className="ml-auto flex-shrink-0 font-medium text-slate-800">
                  {formatCompact(slice.entry.value)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/**
 * Selection plumbing shared by the chart types.
 *
 * `onSelect` is optional: a chart only becomes clickable once the admin has
 * given it a Filter action with at least one target, so a dashboard with no
 * actions behaves exactly as it did before.
 */
interface Selectable {
  onSelect?: (category: string) => void;
  selected?: string | null;
}

/** Dims the categories that are not the selected one. */
function selectionOpacity(selected: string | null | undefined, category: string): number {
  if (!selected) return 1;
  return selected === category ? 1 : 0.32;
}

/* ─────────────────────────── Cartesian charts (SVG) ──────────────────────────
 * Bar, column and line share one geometry model.
 *
 * They are drawn in REAL PIXELS — the viewBox equals the tile's measured size —
 * rather than in a fixed 240x140 box scaled to fit. A fixed box letterboxes: in
 * a tall tile the chart stays short and leaves dead space below, and the bars
 * never get thicker however much room they are given. Measuring instead lets
 * bar thickness follow the tile, which is what "resize the widget and the bars
 * resize with it" actually means.
 * -------------------------------------------------------------------------- */

/** Type sizes are in px and therefore constant at every tile size. */
const FONT = { tick: 11, category: 11, value: 11, axisTitle: 12 };

/** Rounds an axis maximum up to a readable step. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
}

function shortLabel(label: string, maxChars: number): string {
  return label.length > maxChars ? `${label.slice(0, Math.max(1, maxChars - 1))}…` : label;
}

interface Plot {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Chart props shared by the three cartesian types. */
interface CartesianProps extends Selectable {
  data: Datum[];
  /** Measured tile size; the chart draws itself at exactly this size. */
  width: number;
  height: number;
  showValueLabels: boolean;
  xLabel?: string;
  yLabel?: string;
}

/**
 * Axis titles, placed against the outer edges of the measured box.
 *
 * Only rendered when named, and the plot rect below reserves room for them on
 * the same condition — an unused title must not cost a tile any space.
 */
function AxisTitles({
  xLabel,
  yLabel,
  width,
  height,
  plot,
}: {
  xLabel?: string;
  yLabel?: string;
  width: number;
  height: number;
  plot: Plot;
}) {
  return (
    <>
      {xLabel ? (
        <text
          x={plot.left + plot.width / 2}
          y={height - 4}
          textAnchor="middle"
          style={{ fontSize: FONT.axisTitle, fontWeight: 600 }}
          className="fill-slate-600"
        >
          {xLabel}
        </text>
      ) : null}
      {yLabel ? (
        <text
          transform={`rotate(-90 ${FONT.axisTitle} ${plot.top + plot.height / 2})`}
          x={FONT.axisTitle}
          y={plot.top + plot.height / 2}
          textAnchor="middle"
          style={{ fontSize: FONT.axisTitle, fontWeight: 600 }}
          className="fill-slate-600"
        >
          {yLabel}
        </text>
      ) : null}
    </>
  );
}

/** Value gridlines. `axis` says which edge carries the numbers. */
function Gridlines({
  max,
  plot,
  axis,
}: {
  max: number;
  plot: Plot;
  axis: 'y' | 'x';
}) {
  const ticks = [0, 0.5, 1];
  return (
    <g>
      {ticks.map((tick) => {
        if (axis === 'y') {
          const y = plot.top + plot.height * (1 - tick);
          return (
            <g key={tick}>
              <line
                x1={plot.left}
                y1={y}
                x2={plot.left + plot.width}
                y2={y}
                stroke="var(--bv-line)"
                strokeWidth={1}
              />
              <text
                x={plot.left - 6}
                y={y + FONT.tick / 3}
                textAnchor="end"
                style={{ fontSize: FONT.tick }}
                className="fill-slate-400"
              >
                {formatCompact(max * tick)}
              </text>
            </g>
          );
        }
        const x = plot.left + plot.width * tick;
        return (
          <g key={tick}>
            <line
              x1={x}
              y1={plot.top}
              x2={x}
              y2={plot.top + plot.height}
              stroke="var(--bv-line)"
              strokeWidth={1}
            />
            <text
              x={x}
              y={plot.top + plot.height + FONT.tick + 4}
              textAnchor="middle"
              style={{ fontSize: FONT.tick }}
              className="fill-slate-400"
            >
              {formatCompact(max * tick)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/**
 * Bar thickness for `count` bars sharing `span` pixels.
 *
 * Capped so two categories in a tall tile become two readable bars rather than
 * two slabs, and floored so twenty categories stay visible instead of vanishing.
 */
function barThickness(span: number, count: number): number {
  if (count <= 0) return 0;
  return Math.max(3, Math.min(46, (span / count) * 0.62));
}

/* ──────────────────────────── Horizontal bars ───────────────────────────── */

function BarChart({
  data,
  width,
  height,
  palette,
  showValueLabels,
  xLabel,
  yLabel,
  onSelect,
  selected,
}: CartesianProps & { palette?: string[] }) {
  const max = niceMax(Math.max(...data.map((entry) => entry.value), 0));

  /* The category gutter is sized from the longest label, so short codes do not
     waste a third of a narrow tile and long names are not all truncated. */
  const longest = data.reduce((n, entry) => Math.max(n, entry.category.length), 0);
  const gutter = Math.min(Math.max(38, longest * 6.6), Math.max(52, width * 0.32));

  const plot: Plot = {
    left: (yLabel ? FONT.axisTitle + 10 : 6) + gutter,
    top: 10,
    width: 0,
    height: 0,
  };
  plot.width = Math.max(10, width - plot.left - (showValueLabels ? 44 : 12));
  plot.height = Math.max(10, height - plot.top - (FONT.tick + 12) - (xLabel ? FONT.axisTitle + 8 : 0));

  const slot = plot.height / Math.max(data.length, 1);
  const thickness = barThickness(plot.height, data.length);

  return (
    /* Sized by CSS, not by width/height attributes: an explicit height makes
           the SVG an intrinsically sized box that props its own container open,
           so the measurement can grow but never shrink. The viewBox already
           matches the measured box, so the mapping stays exactly 1:1. */
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block h-full w-full"
        role="img"
      >
      <Gridlines max={max} plot={plot} axis="x" />
      <AxisTitles xLabel={xLabel} yLabel={yLabel} width={width} height={height} plot={plot} />
      {data.map((entry, index) => {
        const centre = plot.top + slot * index + slot / 2;
        const length = max > 0 ? (entry.value / max) * plot.width : 0;
        return (
          <g
            key={entry.category}
            opacity={selectionOpacity(selected, entry.category)}
            onClick={onSelect ? () => onSelect(entry.category) : undefined}
            style={onSelect ? { cursor: 'pointer' } : undefined}
          >
            {/* Full-row hit area: a thin bar is a poor click target. */}
            {onSelect && (
              <rect x={0} y={plot.top + slot * index} width={width} height={slot} fill="transparent" />
            )}
            <text
              x={plot.left - 8}
              y={centre + FONT.category / 3}
              textAnchor="end"
              style={{ fontSize: FONT.category }}
              className="fill-slate-600"
            >
              {shortLabel(entry.category, Math.max(4, Math.floor(gutter / 6.6)))}
              <title>{entry.category}</title>
            </text>
            <rect
              x={plot.left}
              y={centre - thickness / 2}
              width={Math.max(length, 1)}
              height={thickness}
              rx={Math.min(3, thickness / 2)}
              fill={paletteColor(palette, index)}
            >
              <title>{`${entry.category}: ${formatNumber(entry.value)}`}</title>
            </rect>
            {showValueLabels && (
              <text
                x={plot.left + length + 6}
                y={centre + FONT.value / 3}
                style={{ fontSize: FONT.value, fontWeight: 600 }}
                className="fill-slate-700"
              >
                {formatCompact(entry.value)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ───────────────────────────── Vertical bars ────────────────────────────── */

function ColumnChart({
  data,
  width,
  height,
  palette,
  showValueLabels,
  xLabel,
  yLabel,
  onSelect,
  selected,
}: CartesianProps & { palette?: string[] }) {
  const max = niceMax(Math.max(...data.map((entry) => entry.value), 0));

  const plot: Plot = { left: (yLabel ? FONT.axisTitle + 10 : 4) + 34, top: 18, width: 0, height: 0 };
  plot.width = Math.max(10, width - plot.left - 12);
  plot.height = Math.max(
    10,
    height - plot.top - (FONT.category + 12) - (xLabel ? FONT.axisTitle + 8 : 0),
  );

  const slot = plot.width / Math.max(data.length, 1);
  const thickness = barThickness(plot.width, data.length);

  return (
    /* Sized by CSS, not by width/height attributes: an explicit height makes
           the SVG an intrinsically sized box that props its own container open,
           so the measurement can grow but never shrink. The viewBox already
           matches the measured box, so the mapping stays exactly 1:1. */
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block h-full w-full"
        role="img"
      >
      <Gridlines max={max} plot={plot} axis="y" />
      <AxisTitles xLabel={xLabel} yLabel={yLabel} width={width} height={height} plot={plot} />
      {data.map((entry, index) => {
        const barHeight = max > 0 ? (entry.value / max) * plot.height : 0;
        const centre = plot.left + slot * index + slot / 2;
        const y = plot.top + plot.height - barHeight;
        return (
          <g
            key={entry.category}
            opacity={selectionOpacity(selected, entry.category)}
            onClick={onSelect ? () => onSelect(entry.category) : undefined}
            style={onSelect ? { cursor: 'pointer' } : undefined}
          >
            {onSelect && (
              <rect x={plot.left + slot * index} y={plot.top} width={slot} height={plot.height} fill="transparent" />
            )}
            <rect
              x={centre - thickness / 2}
              y={y}
              width={thickness}
              height={Math.max(barHeight, 1)}
              rx={Math.min(3, thickness / 2)}
              fill={paletteColor(palette, index)}
            >
              <title>{`${entry.category}: ${formatNumber(entry.value)}`}</title>
            </rect>
            {showValueLabels && (
              <text
                x={centre}
                y={y - 4}
                textAnchor="middle"
                style={{ fontSize: FONT.value, fontWeight: 600 }}
                className="fill-slate-700"
              >
                {formatCompact(entry.value)}
              </text>
            )}
            <text
              x={centre}
              y={plot.top + plot.height + FONT.category + 4}
              textAnchor="middle"
              style={{ fontSize: FONT.category }}
              className="fill-slate-500"
            >
              {shortLabel(entry.category, Math.max(3, Math.floor(slot / 7)))}
              <title>{entry.category}</title>
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ──────────────────────────────── Line ──────────────────────────────────── */

function LineChart({
  data,
  width,
  height,
  color,
  showValueLabels,
  xLabel,
  yLabel,
  onSelect,
  selected,
}: CartesianProps & { color: string }) {
  const max = niceMax(Math.max(...data.map((entry) => entry.value), 0));

  const plot: Plot = { left: (yLabel ? FONT.axisTitle + 10 : 4) + 34, top: 18, width: 0, height: 0 };
  plot.width = Math.max(10, width - plot.left - 12);
  plot.height = Math.max(
    10,
    height - plot.top - (FONT.category + 12) - (xLabel ? FONT.axisTitle + 8 : 0),
  );

  const step = data.length > 1 ? plot.width / (data.length - 1) : 0;
  const points = data.map((entry, index) => ({
    x: plot.left + (data.length > 1 ? step * index : plot.width / 2),
    y: plot.top + plot.height - (max > 0 ? (entry.value / max) * plot.height : 0),
    entry,
  }));

  const line = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = points.length
    ? `${plot.left},${plot.top + plot.height} ${line} ${points[points.length - 1].x},${plot.top + plot.height}`
    : '';

  return (
    /* Sized by CSS, not by width/height attributes: an explicit height makes
           the SVG an intrinsically sized box that props its own container open,
           so the measurement can grow but never shrink. The viewBox already
           matches the measured box, so the mapping stays exactly 1:1. */
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block h-full w-full"
        role="img"
      >
      <Gridlines max={max} plot={plot} axis="y" />
      <AxisTitles xLabel={xLabel} yLabel={yLabel} width={width} height={height} plot={plot} />
      {points.length > 1 && <polygon points={area} fill={color} opacity={0.1} />}
      {points.length > 1 && (
        <polyline points={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
      )}
      {points.map((point) => (
        <g
          key={point.entry.category}
          opacity={selectionOpacity(selected, point.entry.category)}
          onClick={onSelect ? () => onSelect(point.entry.category) : undefined}
          style={onSelect ? { cursor: 'pointer' } : undefined}
        >
          {/* A 4px dot is far too small a target, so a full-height band takes it. */}
          {onSelect && (
            <rect
              x={point.x - (step || plot.width) / 2}
              y={plot.top}
              width={step || plot.width}
              height={plot.height}
              fill="transparent"
            />
          )}
          <circle
            cx={point.x}
            cy={point.y}
            r={selected === point.entry.category ? 6 : 4}
            fill={color}
          >
            <title>{`${point.entry.category}: ${formatNumber(point.entry.value)}`}</title>
          </circle>
          {showValueLabels && (
            <text
              x={point.x}
              y={point.y - 8}
              textAnchor="middle"
              style={{ fontSize: FONT.value, fontWeight: 600 }}
              className="fill-slate-700"
            >
              {formatCompact(point.entry.value)}
            </text>
          )}
          <text
            x={point.x}
            y={plot.top + plot.height + FONT.category + 4}
            textAnchor="middle"
            style={{ fontSize: FONT.category }}
            className="fill-slate-500"
          >
            {shortLabel(point.entry.category, Math.max(3, Math.floor((step || plot.width) / 7)))}
            <title>{point.entry.category}</title>
          </text>
        </g>
      ))}
    </svg>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-xs text-slate-400">No data for this configuration</p>
    </div>
  );
}

/* ──────────────────────────────── Container ─────────────────────────────── */

export default function ChartWidget({
  widget,
  source,
  refreshInterval,
  accentColor,
  dividerColor,
  selectorWhere,
  incomingSignals,
  onClearSignals,
  activeSignal,
  onSignal,
}: Props & ActionTargetProps & ActionSourceProps) {
  const { fields } = useSourceFields(source);
  const problems = validateWidget(widget);

  const statistic = widget.config.statistic ?? 'count';
  const categoryField = widget.config.categoryField ?? '';
  const incoming = useIncomingConstraint(incomingSignals, fields);
  const where = combineWhere(combineWhere(buildWhereClause(widget.filters, fields), incoming.where), selectorWhere ?? '');
  /* Selections leave through here so a spatially-matched action can attach
     the selected shape. Attribute-matched actions pass straight through. */
  const emitSignal = useOutgoingSignal(widget, source, onSignal, where);
  const limit = widget.config.maxCategories ?? 8;
  const enabled = Boolean(source) && problems.length === 0;

  const { data, status, error, reload } = useWidgetData<CategoryResult>(
    () =>
      getDataSourceAdapter(source as DashboardDataSource).fetchCategories(
        source as DashboardDataSource,
        {
          categoryField,
          statistic,
          field: widget.config.field,
          where,
          extent: incoming.extent, geometry: incoming.geometry,
          limit,
          sortBy: widget.config.sortBy,
        },
      ),
    `chart|${source?.id}|${categoryField}|${statistic}|${widget.config.field ?? ''}|${where}|${JSON.stringify(incoming.extent ?? null)}|${incoming.geometry ? 'geom' : ''}|${limit}|${widget.config.sortBy ?? ''}`,
    refreshInterval,
    enabled,
  );

  /* Real pixel size of the plotting area. The cartesian charts need it to size
     their bars; without it a tall tile would show short bars and empty space. */
  const { ref: plotRef, width: plotWidth, height: plotHeight } = useContainerSize<HTMLDivElement>();

  const rows = data?.data ?? [];
  const color = widget.config.color || accentColor;
  const showValueLabels = widget.config.showValueLabels !== false;
  const xLabel = widget.config.xAxisLabel?.trim() || undefined;
  const yLabel = widget.config.yAxisLabel?.trim() || undefined;

  /* One orientation setting drives both bar types. Unset falls back to each
     type's historic shape, so nothing already saved changes appearance. */
  const orientation =
    widget.config.orientation ?? (widget.type === 'column' ? 'vertical' : 'horizontal');

  /* Clicking a category broadcasts it; clicking the SAME one again clears it,
     which is the only discoverable way back to the unfiltered view. */
  const canSelect =
    Boolean(onSignal) &&
    Boolean(categoryField) &&
    (widget.actions ?? []).some(
      (action) => action.trigger === 'selection' && action.targetWidgetIds.length > 0,
    );
  const selected =
    activeSignal && activeSignal.kind === 'selection' ? activeSignal.value : null;
  const handleSelect = canSelect
    ? (category: string) => {
        if (selected === category) {
          onSignal?.(widget.id, null);
          return;
        }
        emitSignal(widget.id, {
          kind: 'selection',
          sourceId: widget.id,
          sourceTitle: widget.title,
          field: categoryField,
          value: category === '(blank)' ? '' : category,
        });
      }
    : undefined;

  const render = (type: WidgetType) => {
    if (rows.length === 0) return <EmptyChart />;
    switch (type) {
      case 'pie':
      case 'donut':
        return (
          <PieChart
            data={rows}
            palette={widget.config.palette}
            donut={type === 'donut'}
            showLegend={widget.config.showLegend !== false}
            onSelect={handleSelect}
            selected={selected}
          />
        );
      case 'bar':
      case 'column': {
        // Nothing can be laid out until the tile has been measured once.
        if (plotWidth < 10 || plotHeight < 10) return null;
        const Chart = orientation === 'vertical' ? ColumnChart : BarChart;
        return (
          <Chart
            data={rows}
            width={plotWidth}
            height={plotHeight}
            palette={widget.config.palette}
            showValueLabels={showValueLabels}
            xLabel={xLabel}
            yLabel={yLabel}
            onSelect={handleSelect}
            selected={selected}
          />
        );
      }
      case 'line':
        if (plotWidth < 10 || plotHeight < 10) return null;
        return (
          <LineChart
            data={rows}
            width={plotWidth}
            height={plotHeight}
            color={color}
            showValueLabels={showValueLabels}
            xLabel={xLabel}
            yLabel={yLabel}
            onSelect={handleSelect}
            selected={selected}
          />
        );
      default:
        return <EmptyChart />;
    }
  };

  return (
    <WidgetFrame
      title={widget.title}
      status={status}
      error={error}
      onRetry={reload}
      incomplete={problems}
      accentColor={accentColor}
      outlineColor={widget.config.outlineColor ?? dividerColor}
      actionLabels={incoming.labels}
      onClearActions={() => onClearSignals?.(incoming.sourceIds)}
      footnote={[
        source?.name,
        categoryField ? `by ${categoryField}` : '',
        data?.truncated ? 'partial — first 5,000 records' : '',
      ]
        .filter(Boolean)
        .join(' · ')}
    >
      {/* The measured box. `min-h-0` matters: without it a flex child refuses to
          shrink and the observer reports a height that never goes down. */}
      <div ref={plotRef} className="h-full min-h-0 w-full">
        {render(widget.type)}
      </div>
    </WidgetFrame>
  );
}
